// ========================================
// SOURCES MANAGEMENT SYSTEM
// ========================================

import { safeLocalStorageSet } from '../utils/storageValidator';
import { APP_VERSION } from '../constants/version';
import { systemBus, EVENTS } from '../services/systemBus';

// Default SRD source that comes with the app
const DEFAULT_SOURCE = {
  id: 'srd',
  name: 'Open SRD',
  author: 'Wizards of the Coast',
  version: '5.1',
  description: 'The D&D 5e System Reference Document - Core rules and content',
  enabled: true,
  removable: false, // Cannot be removed
  editable: false,  // [FIX NOV 22, 2025]: SRD metadata cannot be edited
  builtin: true,    // [CW-TRIO-SOURCES 2026-07-12]: shipped with the app — excluded from user-content listings
  categories: {
    items: true,
    spells: true,
    feats: true, // [PHASE 3.1.4 - NOV 11, 2025]: Enabled for 62 SRD feats
    backgrounds: true, // [PHASE 3.1.4 - NOV 11, 2025]: Enabled for 13 SRD backgrounds
    subclasses: true, // [CRITICAL-5 - NOV 15, 2025]: Enabled for 40 SRD + 48 expansion subclasses
    races: true, // [CONTENT CREATORS - FEB 2026]: Custom races from sources
    classes: true, // [CONTENT CREATORS - FEB 2026]: Custom classes from sources
    abilities: false,
    boons: false,
    homebrewRules: false // [HOMEBREW SYSTEM - JAN 10, 2026]: Custom conditions, modifiers, mechanics
  },
  content: {
    items: {}, // Will be populated from existing gameData.js
    spells: {}, // Will be populated from existing srdSpells.js
    feats: {}, // [PHASE 3.1.4 - NOV 11, 2025]: Will be populated from srdFeats.js
    backgrounds: {}, // [PHASE 3.1.4 - NOV 11, 2025]: Will be populated from srdCharacterData.js
    subclasses: {}, // [CRITICAL-5 - NOV 15, 2025]: Will be populated from srdCharacterData.js and sources
    races: {}, // [CONTENT CREATORS - FEB 2026]: Custom races
    classes: {}, // [CONTENT CREATORS - FEB 2026]: Custom classes
    abilities: {},
    boons: {},
    homebrewRules: {} // [HOMEBREW SYSTEM - JAN 10, 2026]: Custom conditions, numeric modifiers, concealment, etc.
  }
};

// [FIX NOV 22, 2025]: Default Custom source for user content
// [PURPOSE]: Provide a ready-to-use source for homebrew content
// [WHY]: Users shouldn't need to create a source before adding custom content
const DEFAULT_CUSTOM_SOURCE = {
  id: 'custom',
  name: 'Custom',
  author: 'Homebrew',
  version: '1.0',
  description: 'Your custom homebrew content - items, spells, feats, and more',
  enabled: true,
  removable: false, // Always available for custom content
  editable: true,   // Metadata can be customized
  categories: {
    items: true,
    spells: true,
    feats: true,
    backgrounds: true,
    subclasses: true,
    races: true, // [CONTENT CREATORS - FEB 2026]: Custom races
    classes: true, // [CONTENT CREATORS - FEB 2026]: Custom classes
    abilities: false,
    boons: false,
    homebrewRules: true // [HOMEBREW SYSTEM - JAN 10, 2026]: Enable for custom homebrew content
  },
  content: {
    items: {},
    spells: {},
    feats: {},
    backgrounds: {},
    subclasses: {},
    races: {}, // [CONTENT CREATORS - FEB 2026]: Custom races
    classes: {}, // [CONTENT CREATORS - FEB 2026]: Custom classes
    abilities: {},
    boons: {},
    homebrewRules: {} // [HOMEBREW SYSTEM - JAN 10, 2026]: Custom conditions, numeric modifiers, etc.
  }
};

// Sources Manager Class
class SourcesManager {
  constructor() {
    this.sources = this.loadSources();
    this.activeCategories = this.loadActiveCategories();
  }

  // Load sources from localStorage
  loadSources() {
    const stored = localStorage.getItem('tabletop_sources');
    if (stored) {
      try {
        const sources = JSON.parse(stored);

        // [UX-12 - NOV 15, 2025]: Merge stored SRD with DEFAULT_SOURCE to ensure updates
        // [WHY]: Existing users have old SRD without feats/backgrounds/subclasses categories
        // [ISSUE]: User reported "sources show 0 feats/backgrounds" - old localStorage had empty content
        // [FIX]: Deep merge ensures new categories are added while preserving user settings
        if (sources.srd) {
          sources.srd = {
            ...DEFAULT_SOURCE,
            ...sources.srd,
            // Merge categories (preserves user enable/disable but adds new ones from DEFAULT)
            categories: {
              ...DEFAULT_SOURCE.categories,
              ...sources.srd.categories
            },
            // Merge content (allows initializeSRDContent to populate new categories)
            content: {
              ...DEFAULT_SOURCE.content,
              ...sources.srd.content
            }
          };
        } else {
          sources.srd = DEFAULT_SOURCE;
        }

        // [FIX NOV 22, 2025]: Ensure default Custom source exists
        // [WHY]: Provide ready-to-use source for homebrew content
        if (sources.custom) {
          sources.custom = {
            ...DEFAULT_CUSTOM_SOURCE,
            ...sources.custom,
            // Preserve name, author, description if user customized them
            name: sources.custom.name || DEFAULT_CUSTOM_SOURCE.name,
            author: sources.custom.author || DEFAULT_CUSTOM_SOURCE.author,
            version: sources.custom.version || DEFAULT_CUSTOM_SOURCE.version,
            description: sources.custom.description || DEFAULT_CUSTOM_SOURCE.description,
            // Merge categories
            categories: {
              ...DEFAULT_CUSTOM_SOURCE.categories,
              ...sources.custom.categories
            },
            // Preserve user content
            content: {
              ...DEFAULT_CUSTOM_SOURCE.content,
              ...sources.custom.content
            }
          };
        } else {
          sources.custom = DEFAULT_CUSTOM_SOURCE;
        }

        // [v1.1 MIGRATION - APR 2026]: Enable official sourcebooks by default
        // [WHY]: Tasha's/Xanathar's/Fizban's were previously disabled by default.
        // Existing users have them stored as enabled: false. This one-time migration
        // enables them so all players get official content out of the box.
        // [v1.0 MAY 2026]: Added Volo's to the official-enabled set; bumped key
        // to v1.1_enable_official_v2 so users who already ran the original
        // migration get Volo's enabled too.
        const migrationKey = 'tabletop_sources_migration';
        const migrationDone = localStorage.getItem(migrationKey) || '';
        if (!migrationDone.includes('v1.1_enable_official_v2')) {
          const officialSources = ['tashas', 'xanathars', 'fizbans', 'volos'];
          for (const sourceId of officialSources) {
            if (sources[sourceId]) {
              sources[sourceId].enabled = true;
            }
          }
          safeLocalStorageSet(migrationKey, migrationDone + (migrationDone ? ',' : '') + 'v1.1_enable_official_v2');
        }

        // [CW-TRIO-SOURCES 2026-07-12]: Stamp builtin on shipped sources so
        // Workshop Load drawers can list user-authored sourcebooks only.
        // Persisted installs never re-run importSource (it's guarded by
        // presence), so heal the flag here on every load — idempotent.
        for (const builtinId of ['srd', 'tashas', 'xanathars', 'fizbans', 'volos']) {
          if (sources[builtinId]) {
            sources[builtinId].builtin = true;
          }
        }

        return sources;
      } catch (e) {
        console.error('Failed to load sources:', e);
      }
    }
    // [FIX NOV 22, 2025]: Return both default sources
    return {
      srd: DEFAULT_SOURCE,
      custom: DEFAULT_CUSTOM_SOURCE
    };
  }

  // Load active category preferences
  loadActiveCategories() {
    const stored = localStorage.getItem('tabletop_active_categories');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Failed to load active categories:', e);
      }
    }
    // [FIX NOV 15, 2025]: Enable feats and backgrounds by default since SRD has 62 feats and 13 backgrounds
    // [WHY]: User reported "sources show 0 feats" - they were disabled by default
    // [CRITICAL-5 NOV 15, 2025]: Enable subclasses by default for 40 SRD + 48 expansion subclasses
    // [HOMEBREW SYSTEM - JAN 10, 2026]: Enable homebrewRules by default for campaign-specific content
    return {
      items: true,
      spells: true,
      feats: true,        // Changed from false - SRD has 62 feats
      backgrounds: true,  // Changed from false - SRD has 13 backgrounds
      subclasses: true,   // [CRITICAL-5]: Enabled for subclass system (40 SRD + 48 expansion)
      races: true,        // [CONTENT CREATORS - FEB 2026]: Custom races
      classes: true,      // [CONTENT CREATORS - FEB 2026]: Custom classes
      abilities: false,
      boons: false,
      homebrewRules: true // [HOMEBREW SYSTEM]: Custom conditions, numeric modifiers, concealment
    };
  }

  // Save sources to localStorage
  saveSources() {
    safeLocalStorageSet('tabletop_sources', JSON.stringify(this.sources));
  }

  // Save active categories
  saveActiveCategories() {
    safeLocalStorageSet('tabletop_active_categories', JSON.stringify(this.activeCategories));
  }

  // Get all enabled sources
  getEnabledSources() {
    return Object.values(this.sources).filter(source => source.enabled);
  }

  // Get all content from enabled sources for a specific category
  getContentForCategory(category) {
    const enabledSources = this.getEnabledSources();
    const combinedContent = {};

    for (const source of enabledSources) {
      if (source.categories[category] && source.content[category]) {
        Object.assign(combinedContent, source.content[category]);
      }
    }

    return combinedContent;
  }

  // Get all items from enabled sources
  getAllItems() {
    return this.getContentForCategory('items');
  }

  // Get all spells from enabled sources
  getAllSpells() {
    return this.getContentForCategory('spells');
  }

  // Get all feats from enabled sources
  getAllFeats() {
    return this.getContentForCategory('feats');
  }

  // Get all backgrounds from enabled sources
  getAllBackgrounds() {
    return this.getContentForCategory('backgrounds');
  }

  // Get all abilities from enabled sources
  getAllAbilities() {
    return this.getContentForCategory('abilities');
  }

  // Get all boons from enabled sources
  getAllBoons() {
    return this.getContentForCategory('boons');
  }

  // [CRITICAL-5 - NOV 15, 2025]: Get all subclasses from enabled sources
  // [PURPOSE]: Allow LevelUpWizard to query all available subclasses
  getAllSubclasses() {
    return this.getContentForCategory('subclasses');
  }

  // [HOMEBREW SYSTEM - JAN 10, 2026]: Get all homebrew rules from enabled sources
  // [PURPOSE]: Allow ConditionsTracker and other components to query custom conditions/modifiers
  // [TYPES]: 'condition' | 'modifier' | 'mechanic' | 'action_type'
  getAllHomebrewRules() {
    return this.getContentForCategory('homebrewRules');
  }

  // [CONTENT CREATORS - FEB 2026]: Get all races from enabled sources
  // [PURPOSE]: Allow CharacterCreationWizard and CharacterBasicsPanel to query custom races
  getAllRaces() {
    return this.getContentForCategory('races');
  }

  // [CONTENT CREATORS - FEB 2026]: Get all classes from enabled sources
  // [PURPOSE]: Allow CharacterCreationWizard and LevelUpWizard to query custom classes
  getAllClasses() {
    return this.getContentForCategory('classes');
  }

  // [HOMEBREW SYSTEM - JAN 10, 2026]: Get homebrew rules by type
  // [PURPOSE]: Filter homebrew rules by type (e.g., get only custom conditions)
  getHomebrewRulesByType(type) {
    const allRules = this.getAllHomebrewRules();
    const filtered = {};

    for (const [key, rule] of Object.entries(allRules)) {
      if (rule.type === type) {
        filtered[key] = rule;
      }
    }

    return filtered;
  }

  // [HOMEBREW SYSTEM - JAN 10, 2026]: Get custom conditions from homebrew rules
  // [PURPOSE]: ConditionsTracker uses this to load custom conditions
  getCustomConditions() {
    return this.getHomebrewRulesByType('condition');
  }

  // [CRITICAL-5 - NOV 15, 2025]: Get subclasses for a specific class
  // [PURPOSE]: Filter subclasses by class name (e.g., 'Wizard', 'Warlock')
  // [RETURNS]: Object containing only subclasses for the specified class
  getSubclassesForClass(className) {
    const allSubclasses = this.getAllSubclasses();
    const filtered = {};

    for (const [key, subclass] of Object.entries(allSubclasses)) {
      if (subclass.class === className) {
        filtered[key] = subclass;
      }
    }

    return filtered;
  }

  // Add a new source
  addSource(sourceData) {
    const source = {
      id: sourceData.id || `source_${Date.now()}`,
      name: sourceData.name || 'Custom Source',
      author: sourceData.author || 'Unknown',
      version: sourceData.version || '1.0',
      description: sourceData.description || '',
      enabled: sourceData.enabled !== false,
      removable: true,
      editable: sourceData.editable !== false, // [FIX NOV 22, 2025]: New sources are editable by default
      builtin: sourceData.builtin === true, // [CW-TRIO-SOURCES 2026-07-12]: user-created sources stay falsy
      categories: sourceData.categories || {
        items: false,
        spells: false,
        feats: false,
        backgrounds: false,
        subclasses: false, // [CRITICAL-5 - NOV 15, 2025]: Added subclasses category
        races: false, // [CONTENT CREATORS - FEB 2026]: Custom races
        classes: false, // [CONTENT CREATORS - FEB 2026]: Custom classes
        abilities: false,
        boons: false,
        homebrewRules: false // [HOMEBREW SYSTEM - JAN 10, 2026]: Custom conditions, modifiers
      },
      content: sourceData.content || {
        items: {},
        spells: {},
        feats: {},
        backgrounds: {},
        subclasses: {}, // [CRITICAL-5 - NOV 15, 2025]: Added subclasses content
        races: {}, // [CONTENT CREATORS - FEB 2026]: Custom races
        classes: {}, // [CONTENT CREATORS - FEB 2026]: Custom classes
        abilities: {},
        boons: {},
        homebrewRules: {} // [HOMEBREW SYSTEM - JAN 10, 2026]: Custom conditions, modifiers
      }
    };

    this.sources[source.id] = source;
    this.saveSources();
    return source;
  }

  // [FIX NOV 22, 2025]: Update source metadata
  // [PURPOSE]: Allow editing source name, author, version, description
  // [WHY]: Users need to customize their source metadata
  updateSource(sourceId, updates) {
    const source = this.sources[sourceId];
    if (!source) {
      throw new Error('Source not found');
    }

    if (!source.editable) {
      throw new Error('This source cannot be edited');
    }

    // Only allow updating metadata fields
    const allowedFields = ['name', 'author', 'version', 'description'];
    for (const field of allowedFields) {
      if (updates[field] !== undefined) {
        source[field] = updates[field];
      }
    }

    this.saveSources();
    return source;
  }

  // Remove a source
  removeSource(sourceId) {
    if (this.sources[sourceId] && this.sources[sourceId].removable) {
      delete this.sources[sourceId];
      this.saveSources();
      return true;
    }
    return false;
  }

  // Toggle source enabled/disabled
  toggleSource(sourceId, enabled) {
    if (this.sources[sourceId]) {
      this.sources[sourceId].enabled = enabled;
      this.saveSources();
      return true;
    }
    return false;
  }

  // Import a source from JSON
  importSource(jsonData, selectedCategories = null) {
    try {
      const sourceData = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;

      // If categories are selected, filter the content
      if (selectedCategories) {
        const filteredContent = {};
        const filteredCategories = {};

        for (const category of Object.keys(sourceData.categories)) {
          if (selectedCategories[category]) {
            filteredContent[category] = sourceData.content[category] || {};
            filteredCategories[category] = true;
          } else {
            filteredContent[category] = {};
            filteredCategories[category] = false;
          }
        }

        sourceData.content = filteredContent;
        sourceData.categories = filteredCategories;
      }

      // Ensure unique ID
      if (this.sources[sourceData.id]) {
        sourceData.id = `${sourceData.id}_${Date.now()}`;
      }

      return this.addSource(sourceData);
    } catch (e) {
      console.error('Failed to import source:', e);
      throw new Error('Invalid source file format');
    }
  }

  // Export a source to JSON
  exportSource(sourceId) {
    const source = this.sources[sourceId];
    if (!source) {
      throw new Error('Source not found');
    }

    const exportData = {
      ...source,
      exportDate: new Date().toISOString(),
      appVersion: APP_VERSION
    };

    return JSON.stringify(exportData, null, 2);
  }

  // Export multiple sources
  exportSources(sourceIds) {
    const exportData = {
      sources: {},
      exportDate: new Date().toISOString(),
      appVersion: APP_VERSION
    };

    for (const id of sourceIds) {
      if (this.sources[id]) {
        exportData.sources[id] = this.sources[id];
      }
    }

    return JSON.stringify(exportData, null, 2);
  }

  // Create a new custom source
  createSource(name, author = 'Custom', description = '') {
    return this.addSource({
      name,
      author,
      description,
      enabled: true,
      categories: {
        items: true,
        spells: true,
        feats: false,
        backgrounds: false,
        subclasses: false, // [CRITICAL-5 - NOV 15, 2025]: Added subclasses category
        races: false, // [CONTENT CREATORS - FEB 2026]: Custom races
        classes: false, // [CONTENT CREATORS - FEB 2026]: Custom classes
        abilities: false,
        boons: false,
        homebrewRules: false // [HOMEBREW SYSTEM - JAN 10, 2026]: Added homebrewRules category
      },
      content: {
        items: {},
        spells: {},
        feats: {},
        backgrounds: {},
        subclasses: {}, // [CRITICAL-5 - NOV 15, 2025]: Added subclasses content
        races: {}, // [CONTENT CREATORS - FEB 2026]: Custom races
        classes: {}, // [CONTENT CREATORS - FEB 2026]: Custom classes
        abilities: {},
        boons: {},
        homebrewRules: {} // [HOMEBREW SYSTEM - JAN 10, 2026]: Added homebrewRules content
      }
    });
  }

  // Add content to a source
  // [CWR-003 FIX — MAY 2026]: Returns { status: 'created' | 'overwrite' } so
  //   creators can warn the user before clobbering. Pre-fix this silently
  //   overwrote any same-named content (data loss).
  // [BACKWARD COMPAT]: Existing callers passing the result through truthy/falsy
  //   checks still work — both 'created' and 'overwrite' are truthy strings.
  //   Callers wanting to detect collision should compare to 'overwrite'.
  addContentToSource(sourceId, category, contentKey, contentData) {
    const source = this.sources[sourceId];
    if (!source) {
      throw new Error('Source not found');
    }

    if (!source.content[category]) {
      source.content[category] = {};
    }

    const existed = !!source.content[category][contentKey];
    source.content[category][contentKey] = contentData;
    source.categories[category] = true; // Enable category if adding content

    this.saveSources();
    systemBus.emit(EVENTS.SOURCE_CONTENT_CHANGED, {
      sourceId,
      category,
      contentKey,
      action: existed ? 'overwritten' : 'added'
    });
    return existed ? 'overwrite' : 'created';
  }

  // [CWR-003 FIX — MAY 2026]: Lightweight collision check creators can call
  //   BEFORE writing, so they can prompt the user rather than emit a status
  //   toast after the fact.
  hasContentInSource(sourceId, category, contentKey) {
    const source = this.sources[sourceId];
    if (!source || !source.content || !source.content[category]) return false;
    return Object.prototype.hasOwnProperty.call(source.content[category], contentKey);
  }

  // Remove content from a source
  removeContentFromSource(sourceId, category, contentKey) {
    const source = this.sources[sourceId];
    if (!source || !source.content[category]) {
      return false;
    }

    delete source.content[category][contentKey];

    // Check if category is now empty
    if (Object.keys(source.content[category]).length === 0) {
      source.categories[category] = false;
    }

    this.saveSources();
    systemBus.emit(EVENTS.SOURCE_CONTENT_CHANGED, { sourceId, category, contentKey, action: 'removed' });
    return true;
  }

  // Get source info
  getSource(sourceId) {
    return this.sources[sourceId];
  }

  // Get all sources
  getAllSources() {
    return this.sources;
  }

  // Search content across all enabled sources
  searchContent(query, category = null) {
    const results = [];
    const enabledSources = this.getEnabledSources();
    const searchQuery = query.toLowerCase();

    for (const source of enabledSources) {
      const categoriesToSearch = category
        ? [category]
        : Object.keys(source.categories).filter(cat => source.categories[cat]);

      for (const cat of categoriesToSearch) {
        if (source.content[cat]) {
          for (const [key, content] of Object.entries(source.content[cat])) {
            if (key.toLowerCase().includes(searchQuery) ||
                (content.name && content.name.toLowerCase().includes(searchQuery)) ||
                (content.description && content.description.toLowerCase().includes(searchQuery))) {
              results.push({
                source: source.name,
                sourceId: source.id,
                category: cat,
                key: key,
                content: content
              });
            }
          }
        }
      }
    }

    return results;
  }

  // Check if a content item exists in any enabled source
  contentExists(category, contentKey) {
    const enabledSources = this.getEnabledSources();

    for (const source of enabledSources) {
      if (source.categories[category] &&
          source.content[category] &&
          source.content[category][contentKey]) {
        return true;
      }
    }

    return false;
  }

  // Get content item from enabled sources
  getContentItem(category, contentKey) {
    const enabledSources = this.getEnabledSources();

    for (const source of enabledSources) {
      if (source.categories[category] &&
          source.content[category] &&
          source.content[category][contentKey]) {
        return {
          ...source.content[category][contentKey],
          sourceId: source.id,
          sourceName: source.name
        };
      }
    }

    return null;
  }

  // [PHASE 3.1.5 - NOV 11, 2025]: Initialize SRD content from existing data
  // [WHY]: Expanded to include feats and backgrounds for complete SRD migration
  // [CRITICAL-5 - NOV 15, 2025]: Expanded to include subclasses (40 SRD subclasses)
  initializeSRDContent(items, spells, feats, backgrounds, subclasses) {
    if (this.sources.srd) {
      this.sources.srd.content.items = items || {};
      this.sources.srd.content.spells = spells || {};
      this.sources.srd.content.feats = feats || {}; // [PHASE 3.1.5]: Added feats parameter
      this.sources.srd.content.backgrounds = backgrounds || {}; // [PHASE 3.1.5]: Added backgrounds parameter
      this.sources.srd.content.subclasses = subclasses || {}; // [CRITICAL-5]: Added subclasses parameter
      this.saveSources();
    }
  }

  // Get statistics about sources
  getStatistics() {
    const stats = {
      totalSources: Object.keys(this.sources).length,
      enabledSources: this.getEnabledSources().length,
      categories: {
        items: 0,
        spells: 0,
        feats: 0,
        backgrounds: 0,
        subclasses: 0, // [CRITICAL-5 - NOV 15, 2025]: Added subclasses statistics
        races: 0, // [CONTENT CREATORS - FEB 2026]: Custom races count
        classes: 0, // [CONTENT CREATORS - FEB 2026]: Custom classes count
        abilities: 0,
        boons: 0,
        homebrewRules: 0 // [HOMEBREW SYSTEM - JAN 10, 2026]: Added homebrewRules statistics
      }
    };

    for (const source of this.getEnabledSources()) {
      for (const category of Object.keys(stats.categories)) {
        if (source.content[category]) {
          stats.categories[category] += Object.keys(source.content[category]).length;
        }
      }
    }

    return stats;
  }
}

// Create singleton instance
const sourcesManager = new SourcesManager();

// Export the manager instance and class
export { sourcesManager, SourcesManager };

// [PHASE 3.1.5 - NOV 11, 2025]: Helper function to migrate existing data to sources system
// [WHY]: Expanded to include feats and backgrounds migration
// [CRITICAL-5 - NOV 15, 2025]: Expanded to include subclasses migration
export function migrateExistingData(gameData, srdSpells, srdFeats, srdBackgrounds, srdSubclasses) {
  // Convert existing item structure to source format
  const items = {};
  for (const [category, categoryItems] of Object.entries(gameData)) {
    for (const [itemKey, itemData] of Object.entries(categoryItems)) {
      items[`${category}_${itemKey}`] = {
        ...itemData,
        category: category,
        key: itemKey,
        name: itemData.name || itemKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      };
    }
  }

  // Convert existing spell structure to source format
  const spells = {};
  for (const [level, levelSpells] of Object.entries(srdSpells)) {
    for (const spell of levelSpells) {
      const spellKey = spell.name.toLowerCase().replace(/[^a-z0-9]/g, '_');
      spells[spellKey] = {
        ...spell,
        level: parseInt(level),
        key: spellKey
      };
    }
  }

  // [PHASE 3.1.6 - NOV 11, 2025]: Convert SRD feats to source format
  // [PURPOSE]: Migrate 62 feats from srdFeats.js to source system
  const feats = {};
  if (srdFeats) {
    for (const [featKey, featData] of Object.entries(srdFeats)) {
      feats[featKey] = {
        ...featData,
        key: featKey,
        source: 'srd'
      };
    }
  }

  // [PHASE 3.1.7 - NOV 11, 2025]: Convert SRD backgrounds to source format
  // [PURPOSE]: Migrate 13 backgrounds from srdCharacterData.js to source system
  const backgrounds = {};
  if (srdBackgrounds) {
    for (const [bgKey, bgData] of Object.entries(srdBackgrounds)) {
      // Create proper key (lowercase, underscores)
      const backgroundKey = bgKey.toLowerCase().replace(/[^a-z0-9]/g, '_');
      backgrounds[backgroundKey] = {
        ...bgData,
        name: bgKey, // Preserve original name (e.g., "Folk Hero")
        key: backgroundKey,
        source: 'srd'
      };
    }
  }

  // [CRITICAL-5 - NOV 15, 2025]: Convert SRD subclasses to source format
  // [PURPOSE]: Migrate 40 SRD subclasses from srdCharacterData.js to source system
  // [NOTE]: Expansion subclasses (48 from Tasha's/Xanathar's) are already in source files
  const subclasses = {};
  if (srdSubclasses) {
    for (const [subclassKey, subclassData] of Object.entries(srdSubclasses)) {
      subclasses[subclassKey] = {
        ...subclassData,
        key: subclassKey,
        source: 'srd'
      };
    }
  }

  // Initialize SRD content with all categories
  sourcesManager.initializeSRDContent(items, spells, feats, backgrounds, subclasses);

  return sourcesManager;
}